import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import OneSignal from "react-onesignal";

import { getMyUser, GetNotificationsUnreadCounterResponse } from "@/api";
import { initializeOneSignal } from "@/services";
import { useAppStore } from "@/stores";
import { useFavoritesStore } from "@/stores/favorites.store";
import { FavoritesRequest, NotificationEvent, NotificationEventType } from "@/types";

export const NOTIFICATION_EVENT_TYPES_EXCEPTIONS = [NotificationEventType.CHAT_NEW_MESSAGE];

export const notificationsHandler = ({
  queryClient,
  notification: {
    additionalData: { event, eventId },
    launchURL,
  },
  admiredRequest,
  push,
}: NotificationEvent & {
  queryClient: QueryClient;
  admiredRequest: FavoritesRequest;
  push: (href: string) => void;
}) => {
  let toNavigate = true;

  switch (event) {
    case NotificationEventType.USER_ACCOUNT_APPROVED:
    case NotificationEventType.USER_BLOCKED: {
      toNavigate = false;
      break;
    }
    case NotificationEventType.RECEIVED_ADMIRATION: {
      queryClient.invalidateQueries({ queryKey: ["admired", admiredRequest] });
      break;
    }
    case NotificationEventType.EVENT_USER_REMOVED:
    case NotificationEventType.EVENT_DATE_UPDATED:
    case NotificationEventType.EVENT_DATE_AND_LOCATION_UPDATED:
    case NotificationEventType.EVENT_USER_APPROVED:
    case NotificationEventType.EVENT_LOCATION_UPDATED: {
      queryClient.invalidateQueries({ queryKey: [`event`, eventId] });
      break;
    }
    case NotificationEventType.EVENT_CANCELLED: {
      queryClient.invalidateQueries({ queryKey: ["general-notifications"] });
      break;
    }
    case NotificationEventType.REFERRAL_REQUEST_CREATED: {
      queryClient.invalidateQueries({ queryKey: ["referrals"] });
      break;
    }
  }

  if (toNavigate) {
    push(launchURL);
  }
};

export const useInitializeOneSignal = () => {
  const { push } = useRouter();
  const queryClient = useQueryClient();

  const admiredRequest = useFavoritesStore((state) => state["liked-me"].request);
  const increaseNotificationCounter = useAppStore((state) => state.actions.increaseNotificationCounter);
  const decreaseNotificationCounter = useAppStore((state) => state.actions.decreaseNotificationCounter);

  const onClickNotification = (event: any) => {
    const { notification } = event as NotificationEvent;

    if (!NOTIFICATION_EVENT_TYPES_EXCEPTIONS.includes(notification.additionalData.event)) {
      decreaseNotificationCounter();
    }

    notificationsHandler({ notification, admiredRequest, push, queryClient });
  };

  const onDisplayNotification = (event: any) => {
    const {
      notification: {
        additionalData: { event: evenType },
      },
    } = event as NotificationEvent;

    if (NOTIFICATION_EVENT_TYPES_EXCEPTIONS.includes(evenType)) return null;

    increaseNotificationCounter();
    queryClient.setQueryData(
      ["general-notifications-unread-counter"],
      ({ count }: GetNotificationsUnreadCounterResponse) => {
        return { count: count + 1 };
      }
    );
  };

  useEffect(() => {
    initializeOneSignal().then(() => {
      OneSignal.Slidedown.promptPush();

      getMyUser().then((user) => {
        if (user) {
          OneSignal.login(user.id);
        }
      });

      OneSignal.Notifications.addEventListener("click", onClickNotification);
      OneSignal.Notifications.addEventListener("foregroundWillDisplay", onDisplayNotification);
    });

    return () => {
      OneSignal.Notifications.removeEventListener("click", onClickNotification);
      OneSignal.Notifications.removeEventListener("foregroundWillDisplay", onDisplayNotification);
    };
  }, []);
};
